import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import TitleWithBack from '../../components/common/TitleWithBack';
import { formatDate } from '../../utils/helpers';

function ScheduleExam() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    examDate: '',
    module: '',
    examSeries: '',
    startTime: '',
    durationHours: ''
  });
  const [seriesList, setSeriesList] = useState([]);
  const [modules, setModules] = useState([]);
  const [filteredModules, setFilteredModules] = useState([]);
  const [tooltip, setTooltip] = useState('');
  const [existingExam, setExistingExam] = useState(null);
  const [loadingModules, setLoadingModules] = useState(false);

  useEffect(() => {
    async function fetchSeries() {
      try {
        const res = await axios.get('/api/exam-series');
        setSeriesList(Array.isArray(res.data) ? res.data : (res.data.content || []));
      } catch (err) {
        setTooltip('Error fetching exam series.');
      }
    }
    fetchSeries();
  }, []);

  // Fetch modules when exam series is selected
  useEffect(() => {
    async function fetchModulesForSeries() {
      if (!form.examSeries) {
        setModules([]);
        setFilteredModules([]);
        return;
      }
      
      setLoadingModules(true);
      try {
        const modulesRes = await axios.get(`/api/modules/exam-series/${form.examSeries}`);
        const modArray = Array.isArray(modulesRes.data)
          ? modulesRes.data
          : (modulesRes.data.data || []);
        setModules(modArray);
      } catch (err) {
        setTooltip('Error fetching modules for this exam series.');
        setModules([]);
      }
      setLoadingModules(false);
    }
    
    fetchModulesForSeries();
  }, [form.examSeries]);

  // Filter modules based on selected series
  useEffect(() => {
    async function filterModules() {
      if (!form.examSeries || modules.length === 0) {
        setFilteredModules([]);
        return;
      }
      
      // Filter out modules that already have an exam in this series
      const availableModules = [];
      for (const m of modules) {
        try {
          const res = await axios.get(`/api/exams/module/${m.id}/series/${form.examSeries}/existing`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (!res.data) {
            availableModules.push(m);
          }
        } catch (err) {
          // If 404, means no exam exists for this module in this series
          if (err.response && err.response.status === 404) {
            availableModules.push(m);
          }
        }
      }
      setFilteredModules(availableModules);
    }
    
    if (modules.length > 0 && form.examSeries) {
      filterModules();
    } else {
      setFilteredModules([]);
    }
  }, [form.examSeries, modules]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setTooltip('');
    if (name === 'examSeries') {
      setForm(prev => ({ ...prev, module: '' }));
      setExistingExam(null);
    }
    if (name === 'module') {
      setExistingExam(null);
      setTooltip('');
      if (value && form.examSeries) {
        checkExistingExamOnModuleSelect(parseInt(value, 10), form.examSeries);
      }
    }
  };

  const checkExistingExam = async (moduleId, examSeriesId) => {
    try {
      const response = await axios.get(`/api/exams/module/${moduleId}/series/${examSeriesId}/existing`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        return null; // No existing exam found
      }
      throw err;
    }
  };

  const checkExistingExamOnModuleSelect = async (moduleId, examSeriesId) => {
    try {
      const existingExamData = await checkExistingExam(moduleId, examSeriesId);
      if (existingExamData) {
        setExistingExam(existingExamData);
        setTooltip('Warning: An exam for this module already exists in the selected exam series. You can reschedule it instead of creating a new one.');
      }
    } catch (err) {
      console.error('Error checking existing exam:', err);
    }
  };

  const submitExam = async () => {
    const payload = {
      examDate: form.examDate,
      module: { id: parseInt(form.module, 10) },
      examSeries: { id: parseInt(form.examSeries, 10) },
      startTime: form.startTime,
      durationMinutes: Math.round(parseFloat(form.durationHours) * 60)
    };
    try {
      await axios.post('/api/exams', payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setTooltip('Exam scheduled successfully!');
      setTimeout(() => navigate('/admin/exams/view'), 2000);
    } catch (err) {
      console.error(err);
      if (err.response && err.response.data && err.response.data.message) {
        const errorMessage = err.response.data.message;
        if (errorMessage.includes('already exists in the selected exam series')) {
          setTooltip('Error: ' + errorMessage + ' You can reschedule the existing exam instead.');
          // Try to fetch the existing exam details
          try {
            const existingExamData = await checkExistingExam(parseInt(form.module, 10), form.examSeries);
            if (existingExamData) {
              setExistingExam(existingExamData);
            }
          } catch (fetchErr) {
            console.error('Error fetching existing exam details:', fetchErr);
          }
        } else {
          setTooltip('Error: ' + errorMessage);
        }
      } else {
        setTooltip('Error scheduling exam.');
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitExam();
  };

  const handleReschedule = () => {
    if (existingExam) {
      navigate(`/admin/exams/edit/${existingExam.id}`);
    }
  };

  return (
    <div className="container mt-2">
      <TitleWithBack title="Schedule Exam" />
      <div className="card shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Exam Series</label>
              <select
                name="examSeries"
                className="form-select"
                value={form.examSeries}
                onChange={handleChange}
                required
              >
                <option value="">Select Exam Series</option>
                {seriesList.map(s => {
                  const isExpired = new Date(s.endDate) < new Date();
                  return (
                    <option key={s.id} value={s.id} disabled={isExpired}>
                    {s.name} ({formatDate(s.startDate)} to {formatDate(s.endDate)}){isExpired ? ' - Expired' : ''}
                  </option>
                  
                  );
                })}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Exam Date</label>
              <input 
                type="date" 
                name="examDate" 
                className="form-control"
                value={form.examDate}
                onChange={handleChange}
                required
              />
              {form.examDate && (
               <small className="text-muted">Exam Date: {formatDate(form.examDate)}</small>
             )}
            </div>
            <div className="mb-3">
              <label className="form-label">Module</label>
              <select 
                name="module" 
                className="form-select"
                value={form.module}
                onChange={handleChange}
                required
                disabled={!form.examSeries || loadingModules}
              >
                <option value="">{loadingModules ? 'Loading modules...' : 'Select Module'}</option>
                {filteredModules.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.moduleName}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Start Time</label>
              <input
                type="time"
                name="startTime"
                className="form-control"
                value={form.startTime}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Duration (hours)</label>
              <input
                type="number"
                name="durationHours"
                className="form-control"
                value={form.durationHours}
                onChange={handleChange}
                min="0.25"
                step="0.25"
                required
              />
               <small className="form-text text-muted">
                For example 1.00 = 60 minutes,  1.25 = 75 minutes, 1.5 = 90 minutes
               </small>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={existingExam !== null || !form.examSeries || !form.module}
            >
              {existingExam ? 'Exam Already Exists' : 'Schedule Exam'}
            </button>
          </form>

          {tooltip && (
            <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' :
              tooltip.startsWith('Warning') ? 'alert-warning' :
              'alert-success'
            }`}>
              {tooltip}
              {existingExam && (
                <div className="mt-2">
                  <p className="mb-2"><strong>Existing Exam Details:</strong></p>
                  <p className="mb-1">Module: {existingExam.module?.moduleName}</p>
                  <p className="mb-1">Date: {formatDate(existingExam.examDate)}</p>
                  <p className="mb-2">Status: {existingExam.status}</p>
                  <button
                    onClick={handleReschedule}
                    className="btn btn-warning btn-sm"
                  >
                    Reschedule Existing Exam
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScheduleExam;
